import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { coordinatorAPI, formatError } from "../utils/api";
import {
  Truck, CheckCircle, Clock, AlertTriangle, MapPin, Phone,
  RefreshCw, LogOut, Camera, Mic, StopCircle, Plus, X,
  List, Map as MapIcon, Navigation, CheckSquare, Square,
  ChevronRight, Download, Flag, ChevronDown, ChevronUp,
  Activity, BarChart3, Star, TrendingUp, Circle
} from "lucide-react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9e7e0d6e-4f16-4092-b7c7-18a05794ec67/artifacts/w0k6jgr2_WhatsApp%20Image%202026-04-07%20at%2017.12.34.jpeg";
const HYD = { lat: 17.3850, lng: 78.4867 };

const SEV = { Low:"bg-gray-100 text-gray-600", Medium:"bg-orange-100 text-orange-700", High:"bg-red-100 text-red-700" };
const CAT = {
  "Food Quality":"bg-red-50 text-red-700","Quantity":"bg-orange-50 text-orange-700",
  "Delivery Time":"bg-amber-50 text-amber-700","Vessel Condition":"bg-blue-50 text-blue-700",
  "Staff Behaviour":"bg-purple-50 text-purple-700","No Issues":"bg-green-50 text-green-700",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:true,timeZone:"Asia/Kolkata"}); }
  catch { return iso; }
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN",{day:"2-digit",month:"short",timeZone:"Asia/Kolkata"}); }
  catch { return iso; }
}

// ── Circular Progress Ring ──────────────────────────────────────────────────
function Ring({ pct, size=72, stroke=7, color="#E8956D", children }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <div className="relative flex items-center justify-center" style={{width:size,height:size}}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F4F3F0" strokeWidth={stroke}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{transition:"stroke-dashoffset 0.6s ease"}}/>
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}

// ── Route Status Pill ───────────────────────────────────────────────────────
function RoutePill({ r }) {
  const color = r.returned ? "bg-green-500" : r.departed ? "bg-amber-400" : "bg-[#E5E1DB]";
  const textColor = r.returned ? "text-green-700" : r.departed ? "text-amber-700" : "text-[#8A7E73]";
  return (
    <div className={`flex-shrink-0 text-center px-3 py-2 rounded-2xl border ${r.returned ? "border-green-200 bg-green-50" : r.departed ? "border-amber-200 bg-amber-50" : "border-[#E5E1DB] bg-white"}`}>
      <div className={`w-2 h-2 rounded-full mx-auto mb-1 ${color}`}/>
      <p className={`text-xs font-bold ${textColor}`}>{r.route_number}</p>
      <p className="text-[9px] text-[#8A7E73]">{r.returned ? "Back" : r.departed ? fmtTime(r.departure_time) : "Pending"}</p>
    </div>
  );
}

// ══ HOME TAB — Morning Briefing ═════════════════════════════════════════════
function HomeTab({ myRoutes }) {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBriefing = useCallback(async () => {
    try { const r = await coordinatorAPI.getBriefing(); setBriefing(r.data); }
    catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchBriefing(); }, [fetchBriefing]);

  const now = new Date();
  const hour = now.toLocaleString("en-IN",{hour:"numeric",hour12:false,timeZone:"Asia/Kolkata"});
  const greeting = parseInt(hour) < 12 ? "Good morning" : parseInt(hour) < 17 ? "Good afternoon" : "Good evening";
  const today = now.toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",timeZone:"Asia/Kolkata"});

  if (loading) return <div className="flex justify-center py-16"><RefreshCw size={28} className="animate-spin text-[#E8956D]"/></div>;
  if (!briefing?.coordinator_found) return (
    <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
      <AlertTriangle size={40} className="mx-auto mb-3 text-[#C4BAAF]"/>
      <p className="font-bold text-[#3A322C]">No routes assigned</p>
      <p className="text-sm text-[#8A7E73] mt-1">Contact admin to link your coordinator profile.</p>
    </div>
  );

  const b = briefing;
  const dispatchPct = b.total_routes ? Math.round((b.departed_count / b.total_routes) * 100) : 0;
  const returnPct = b.total_routes ? Math.round((b.returned_count / b.total_routes) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* Greeting */}
      <div>
        <h2 className="text-2xl font-extrabold text-[#3A322C] leading-tight">{greeting}, {b.coordinator_name}!</h2>
        <p className="text-sm text-[#8A7E73] mt-0.5">{today}</p>
      </div>

      {/* Big status rings */}
      <div className="bg-white rounded-3xl shadow-sm p-5">
        <div className="flex items-center justify-around gap-4">
          <div className="text-center">
            <Ring pct={dispatchPct} color="#E8956D">
              <div>
                <p className="text-lg font-extrabold text-[#3A322C] leading-none">{b.departed_count}</p>
                <p className="text-[9px] text-[#8A7E73]">of {b.total_routes}</p>
              </div>
            </Ring>
            <p className="text-xs font-bold text-[#5C5147] mt-2">Dispatched</p>
          </div>
          <div className="text-center">
            <Ring pct={returnPct} color="#637C64">
              <div>
                <p className="text-lg font-extrabold text-[#3A322C] leading-none">{b.returned_count}</p>
                <p className="text-[9px] text-[#8A7E73]">of {b.departed_count}</p>
              </div>
            </Ring>
            <p className="text-xs font-bold text-[#5C5147] mt-2">Returned</p>
          </div>
          <div className="text-center">
            <Ring pct={b.total_routes ? Math.round((b.visits_today / Math.max(b.total_routes,1)) * 100) : 0} color="#7C6DBE">
              <p className="text-xl font-extrabold text-[#3A322C]">{b.visits_today}</p>
            </Ring>
            <p className="text-xs font-bold text-[#5C5147] mt-2">Visits</p>
          </div>
        </div>
      </div>

      {/* Alert banners */}
      {b.unacknowledged_alerts > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3 flex items-center gap-3">
          <AlertTriangle size={18} className="text-red-500 flex-shrink-0"/>
          <p className="text-sm font-bold text-red-700">{b.unacknowledged_alerts} complaint alert{b.unacknowledged_alerts>1?"s":""} need your attention</p>
        </div>
      )}
      {b.pending_loading_count > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-center gap-3">
          <Clock size={18} className="text-amber-600 flex-shrink-0"/>
          <p className="text-sm font-bold text-amber-700">
            {b.pending_loading_count} route{b.pending_loading_count>1?"s":""} not yet confirmed loaded: {b.pending_loading_routes.join(", ")}
          </p>
        </div>
      )}
      {b.pending_return > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 flex items-center gap-3">
          <Truck size={18} className="text-blue-600 flex-shrink-0"/>
          <p className="text-sm font-bold text-blue-700">{b.pending_return} vehicle{b.pending_return>1?"s":""} still out — awaiting return</p>
        </div>
      )}

      {/* Route status pills */}
      <div>
        <p className="text-xs font-bold uppercase text-[#8A7E73] mb-3">Today's Routes</p>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {(b.route_status || []).sort((a,b)=>Number(a.route_number)-Number(b.route_number)).map(r => (
            <RoutePill key={r.route_number} r={r}/>
          ))}
        </div>
        <div className="flex gap-4 mt-3 text-xs text-[#8A7E73]">
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-500"/>Returned</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-amber-400"/>Out</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#E5E1DB]"/>Pending</span>
        </div>
      </div>

      {/* Recent complaints */}
      {b.recent_complaints?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-[#F4F3F0]">
            <p className="font-bold text-[#3A322C] text-sm">Open Complaints (7 days)</p>
          </div>
          <div className="divide-y divide-[#F4F3F0]">
            {b.recent_complaints.map((c,i) => (
              <div key={i} className="px-5 py-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#3A322C] truncate">{c.school_name}</p>
                  <p className="text-xs text-[#8A7E73]">Route {c.route_number} · {fmtDate(c.created_at)}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {c.feedback_category && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${CAT[c.feedback_category]||"bg-[#F4F3F0] text-[#5C5147]"}`}>{c.feedback_category}</span>}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${SEV[c.complaint_severity]||SEV.Low}`}>{c.complaint_severity}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button onClick={fetchBriefing} className="w-full py-3 flex items-center justify-center gap-2 text-[#E8956D] font-bold rounded-2xl border-2 border-[#E8956D]/30 text-sm">
        <RefreshCw size={14}/> Refresh Briefing
      </button>
    </div>
  );
}

// ══ DISPATCH TAB ════════════════════════════════════════════════════════════
function DispatchTab({ myRoutes }) {
  const [scope, setScope] = useState("mine");
  const [dispatch, setDispatch] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async (s) => {
    setLoading(true);
    try { const r = await coordinatorAPI.getDispatch({ scope:s }); setDispatch(r.data); }
    catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetch(scope); }, []); // eslint-disable-line

  const byRoute = {};
  (dispatch?.entries||[]).forEach(e => {
    const rn = e.route_number||"?"; if(!byRoute[rn]) byRoute[rn]=[]; byRoute[rn].push(e);
  });
  const sorted = Object.keys(byRoute).sort((a,b)=>Number(a)-Number(b));
  const myNums = new Set(myRoutes?.route_numbers||[]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 p-1 bg-white rounded-2xl shadow-sm">
        {[["mine","My Routes"],["all","All Routes"]].map(([v,l])=>(
          <button key={v} onClick={()=>{setScope(v);fetch(v);}}
            className={`flex-1 py-3 rounded-xl text-sm font-bold ${scope===v?"bg-[#E8956D] text-white shadow":"text-[#8A7E73]"}`}>{l}</button>
        ))}
      </div>
      {dispatch && (
        <div className="grid grid-cols-3 gap-3">
          {[{l:"Trips",v:dispatch.total||0,c:"text-[#3A322C]"},{l:"Out",v:(dispatch.entries||[]).filter(e=>e.direction==="outward").length,c:"text-[#E8956D]"},{l:"Back",v:(dispatch.entries||[]).filter(e=>e.direction==="inward").length,c:"text-[#637C64]"}].map(s=>(
            <div key={s.l} className="bg-white rounded-2xl p-4 text-center shadow-sm">
              <p className={`text-3xl font-extrabold ${s.c}`}>{s.v}</p>
              <p className="text-xs text-[#8A7E73] mt-1 font-medium">{s.l}</p>
            </div>
          ))}
        </div>
      )}
      {loading ? <div className="flex justify-center py-12"><RefreshCw size={28} className="animate-spin text-[#E8956D]"/></div>
      : sorted.length===0 ? (
        <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
          <Truck size={48} className="mx-auto mb-4 text-[#C4BAAF]"/>
          <p className="font-bold text-[#3A322C] text-lg">No trips logged today yet</p>
        </div>
      ) : sorted.map(rn => {
        const entries=byRoute[rn], out=entries.find(e=>e.direction==="outward"), inw=entries.find(e=>e.direction==="inward"), isMine=myNums.has(String(rn));
        return (
          <div key={rn} className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="flex items-center px-5 py-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-extrabold text-base mr-4 ${isMine?"bg-[#E8956D]/10 text-[#E8956D]":"bg-[#F4F3F0] text-[#5C5147]"}`}>{rn}</div>
              <div className="flex-1">
                <p className="font-bold text-[#3A322C]">Route {rn}</p>
                {out?.vehicle_number && <p className="text-xs font-mono text-[#8A7E73]">{out.vehicle_number}</p>}
              </div>
              <div className="text-right">
                {out && <div><p className="text-xs text-[#E8956D] font-bold">Departed</p><p className="font-bold text-[#3A322C]">{fmtTime(out.entry_time)}</p></div>}
                {inw && <div className="mt-1"><p className="text-xs text-[#637C64] font-bold">Returned</p><p className="font-bold text-[#3A322C]">{fmtTime(inw.entry_time)}</p></div>}
                {out&&!inw && <div className="mt-1 flex items-center gap-1 justify-end"><Clock size={13} className="text-amber-500"/><span className="text-xs text-amber-600 font-bold">Out</span></div>}
              </div>
            </div>
          </div>
        );
      })}
      <button onClick={()=>fetch(scope)} className="w-full py-4 flex items-center justify-center gap-2 text-[#E8956D] font-bold rounded-2xl border-2 border-[#E8956D]/30 text-sm"><RefreshCw size={14}/> Refresh</button>
    </div>
  );
}

// ══ VESSELS TAB ═════════════════════════════════════════════════════════════
const VKEYS = ["count_5L","count_10L","count_20L","count_30L","count_40L","egg_tray","banana_tray"];
const VLABELS = {count_5L:"5L",count_10L:"10L",count_20L:"20L",count_30L:"30L",count_40L:"40L",egg_tray:"Egg Tray",banana_tray:"Banana Tray"};

function VesselsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);
  const [counts, setCounts] = useState({});
  const [checked, setChecked] = useState({});
  const [notes, setNotes] = useState({});
  const [msg, setMsg] = useState("");

  const fetchVessels = useCallback(async()=>{setLoading(true);try{const r=await coordinatorAPI.getVesselLoading();setData(r.data);}catch(e){}setLoading(false);},[]);
  useEffect(()=>{fetchVessels();},[fetchVessels]);

  const totalOut = (data?.routes||[]).reduce((s,r)=>s+r.vessels.reduce((a,v)=>a+(parseInt(v.quantity)||0),0),0);
  const confirmedRoutes = (data?.routes||[]).filter(r=>r.is_confirmed).length;

  const handleAllLoaded = async (route) => {
    const allChecked = {};
    VKEYS.forEach(k=>{ allChecked[k]=true; });
    setChecked(c=>({...c,[route.route_id]:allChecked}));
  };

  const handleConfirm = async (route) => {
    setConfirming(route.route_id); setMsg("");
    try {
      const routeCounts = {};
      VKEYS.forEach(k=>{
        const custom = (counts[route.route_id]||{})[k];
        routeCounts[k] = custom !== undefined ? custom : (route.vessels.find(v=>v.key===k)?.quantity||0);
      });
      await coordinatorAPI.confirmLoading(route.route_id,{vessel_counts:routeCounts,confirmed_items:checked[route.route_id]||{},notes:notes[route.route_id]||""});
      setMsg(`Route ${route.route_number} confirmed!`); await fetchVessels();
    } catch(e){setMsg("Error: "+formatError(e));}
    setConfirming(null);
  };

  if (loading) return <div className="flex justify-center py-16"><RefreshCw size={28} className="animate-spin text-[#E8956D]"/></div>;
  if (!data?.routes?.length) return <div className="bg-white rounded-2xl p-8 text-center shadow-sm"><Truck size={48} className="mx-auto mb-4 text-[#C4BAAF]"/><p className="font-bold text-[#3A322C]">No routes assigned</p></div>;

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3">
        {[{l:"Routes",v:data.routes.length,c:"text-[#3A322C]"},{l:"Confirmed",v:confirmedRoutes,c:"text-[#637C64]"},{l:"Vessels Out",v:totalOut,c:"text-[#E8956D]"}].map(s=>(
          <div key={s.l} className="bg-white rounded-2xl p-3 text-center shadow-sm">
            <p className={`text-2xl font-extrabold ${s.c}`}>{s.v}</p>
            <p className="text-[10px] text-[#8A7E73] font-medium">{s.l}</p>
          </div>
        ))}
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-xs text-amber-800 font-medium">Morning checklist — verify vessel counts and tap each to confirm loaded.</div>
      {msg && <div className="bg-[#EDF2EE] text-[#637C64] rounded-2xl px-4 py-2.5 font-bold text-sm">{msg}</div>}

      {data.routes.map(route => {
        const isConf = route.is_confirmed;
        return (
          <div key={route.route_id} className={`bg-white rounded-2xl shadow-sm overflow-hidden ${isConf?"ring-2 ring-[#637C64]":""}`} data-testid={`vessel-route-${route.route_number}`}>
            <div className={`flex items-center px-5 py-4 ${isConf?"bg-[#EDF2EE]":"bg-[#FAFAF8]"}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-extrabold text-base mr-4 ${isConf?"bg-[#637C64]/10 text-[#637C64]":"bg-[#E8956D]/10 text-[#E8956D]"}`}>{route.route_number}</div>
              <div className="flex-1">
                <p className="font-bold text-[#3A322C]">Route {route.route_number}</p>
                {route.vehicle_number && <p className="text-xs font-mono text-[#8A7E73]">{route.vehicle_number} · {route.driver_name}</p>}
              </div>
              {isConf && <div className="text-right"><span className="flex items-center gap-1 text-xs font-bold text-[#637C64]"><CheckCircle size={14}/>Loaded</span><p className="text-[10px] text-[#8A7E73]">{route.confirmed_by}</p></div>}
            </div>
            {!isConf && (
              <div className="px-5 pb-5 pt-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {route.vessels.map(v=>{
                    const isChk=(checked[route.route_id]||{})[v.key];
                    const qty=(counts[route.route_id]||{})[v.key]!==undefined?(counts[route.route_id]||{})[v.key]:v.quantity;
                    return (
                      <button key={v.key} onClick={()=>setChecked(c=>({...c,[route.route_id]:{...(c[route.route_id]||{}),[v.key]:!isChk}}))}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 min-h-[56px] ${isChk?"border-[#637C64] bg-[#EDF2EE]":"border-[#E5E1DB] bg-[#FAFAF8]"}`}>
                        {isChk?<CheckSquare size={18} className="text-[#637C64] flex-shrink-0"/>:<Square size={18} className="text-[#C4BAAF] flex-shrink-0"/>}
                        <div className="text-left flex-1">
                          <p className="text-xs font-bold text-[#3A322C]">{v.label}</p>
                          <input type="number" min="0" className="w-14 text-sm font-bold bg-transparent outline-none text-[#E8956D]"
                            value={qty||""}
                            onClick={e=>e.stopPropagation()}
                            onChange={e=>setCounts(c=>({...c,[route.route_id]:{...(c[route.route_id]||{}),[v.key]:parseInt(e.target.value)||0}}))}/>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <button onClick={()=>handleAllLoaded(route)} className="flex-1 border-2 border-[#637C64] text-[#637C64] rounded-xl py-2.5 text-sm font-bold">All Loaded ✓</button>
                  <input className="flex-1 border border-[#E5E1DB] rounded-xl px-3 py-2 text-sm outline-none" placeholder="Notes…" value={notes[route.route_id]||""} onChange={e=>setNotes(n=>({...n,[route.route_id]:e.target.value}))}/>
                </div>
                <button onClick={()=>handleConfirm(route)} disabled={confirming===route.route_id}
                  className="w-full bg-[#E8956D] text-white rounded-xl py-4 font-bold text-base flex items-center justify-center gap-2 disabled:opacity-60 min-h-[56px]">
                  {confirming===route.route_id?<RefreshCw size={18} className="animate-spin"/>:<CheckCircle size={18}/>}
                  Mark Route {route.route_number} as Loaded
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ══ VISITS TAB ══════════════════════════════════════════════════════════════
function VisitsTab({ myRoutes }) {
  const [schools, setSchools] = useState([]);
  const [view, setView] = useState("log");
  const [form, setForm] = useState({ school_id:"", visit_type:"Routine", observations:"" });
  const [photos, setPhotos] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [recording, setRecording] = useState(false);
  const [recTime, setRecTime] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(""); const [error, setError] = useState("");
  const [visits, setVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const mediaRef = useRef(null); const timerRef = useRef(null); const photoRef = useRef(null);

  useEffect(()=>{coordinatorAPI.getSchools().then(r=>setSchools(r.data?.schools||[])).catch(()=>{});},[]);
  useEffect(()=>{
    if(view==="history"){setLoadingVisits(true);coordinatorAPI.getVisits({}).then(r=>setVisits(r.data||[])).catch(()=>{}).finally(()=>setLoadingVisits(false));}
  },[view]);

  const startRec = async()=>{
    try{const stream=await navigator.mediaDevices.getUserMedia({audio:true});const mr=new MediaRecorder(stream);const chunks=[];mr.ondataavailable=e=>chunks.push(e.data);mr.onstop=()=>{const blob=new Blob(chunks,{type:"audio/webm"});setRecordings(r=>[...r,{blob,url:URL.createObjectURL(blob),duration:recTime,name:`rec_${Date.now()}.webm`}]);stream.getTracks().forEach(t=>t.stop());clearInterval(timerRef.current);setRecTime(0);};mediaRef.current=mr;mr.start();setRecording(true);timerRef.current=setInterval(()=>setRecTime(t=>t+1),1000);}catch(e){setError("Mic denied: "+e.message);}
  };
  const stopRec=()=>{if(mediaRef.current?.state!=="inactive")mediaRef.current?.stop();setRecording(false);};

  const submit=async()=>{
    if(!form.school_id){setError("Select a school");return;}
    if(!form.observations.trim()){setError("Add observations");return;}
    setSubmitting(true);setError("");setSuccess("");
    const fd=new FormData();fd.append("school_id",form.school_id);
    const s=schools.find(x=>x.id===form.school_id);fd.append("school_name",s?.school_name||"");fd.append("route_number",s?.route_number||"");fd.append("visit_type",form.visit_type);fd.append("observations",form.observations);
    try{const p=await new Promise((res,rej)=>navigator.geolocation.getCurrentPosition(res,rej,{timeout:5000}));fd.append("lat",p.coords.latitude.toString());fd.append("lng",p.coords.longitude.toString());}catch(e){}
    photos.forEach(f=>fd.append("photos",f,f.name));recordings.forEach(r=>fd.append("recordings",r.blob,r.name));
    try{await coordinatorAPI.submitVisit(fd);setSuccess("Visit logged!");setForm({school_id:"",visit_type:"Routine",observations:""});setPhotos([]);setRecordings([]);}catch(e){setError(formatError(e));}
    setSubmitting(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 p-1 bg-white rounded-2xl shadow-sm">
        {[["log","Log Visit"],["history","History"]].map(([v,l])=>(
          <button key={v} onClick={()=>setView(v)} className={`flex-1 py-3 rounded-xl text-sm font-bold ${view===v?"bg-[#E8956D] text-white shadow":"text-[#8A7E73]"}`}>{l}</button>
        ))}
      </div>
      {view==="log" && (
        <div className="space-y-4">
          {success && <div className="bg-[#EDF2EE] text-[#637C64] rounded-2xl px-4 py-3 font-bold">{success}</div>}
          {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-2xl px-4 py-3 font-bold">{error}</div>}
          <div className="bg-white rounded-2xl shadow-sm p-5 space-y-4">
            <select className="w-full border-2 border-[#E5E1DB] rounded-xl px-4 py-3.5 text-base outline-none focus:border-[#E8956D] min-h-[56px]"
              value={form.school_id} onChange={e=>setForm(f=>({...f,school_id:e.target.value}))}>
              <option value="">Select school…</option>
              {schools.map(s=><option key={s.id} value={s.id}>{s.school_name} (Route {s.route_number})</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
              {["Routine","Complaint Follow-up","Quality Check","Emergency"].map(t=>(
                <button key={t} onClick={()=>setForm(f=>({...f,visit_type:t}))} className={`py-3.5 rounded-xl text-sm font-bold border-2 min-h-[52px] ${form.visit_type===t?"border-[#E8956D] bg-[#E8956D]/10 text-[#E8956D]":"border-[#E5E1DB] text-[#5C5147]"}`}>{t}</button>
              ))}
            </div>
            <textarea rows={4} className="w-full border-2 border-[#E5E1DB] rounded-xl px-4 py-3 text-base outline-none focus:border-[#E8956D] resize-none" placeholder="Observations…" value={form.observations} onChange={e=>setForm(f=>({...f,observations:e.target.value}))}/>
            <input ref={photoRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={e=>{setPhotos(p=>[...p,...Array.from(e.target.files||[])]);e.target.value="";}}/>
            <button onClick={()=>photoRef.current?.click()} className="w-full min-h-[56px] flex items-center justify-center gap-3 border-2 border-dashed border-[#E8956D]/50 rounded-xl text-[#E8956D] font-bold"><Camera size={22}/> Camera ({photos.length})</button>
            {!recording?<button onClick={startRec} className="w-full min-h-[56px] flex items-center justify-center gap-3 border-2 border-dashed border-blue-400/50 rounded-xl text-blue-600 font-bold"><Mic size={22}/> Record Audio</button>
            :<button onClick={stopRec} className="w-full min-h-[56px] flex items-center justify-center gap-3 bg-red-500 text-white rounded-xl font-bold animate-pulse"><StopCircle size={22}/> Stop ({recTime}s)</button>}
          </div>
          <button onClick={submit} disabled={submitting} className="w-full min-h-[60px] bg-[#E8956D] text-white rounded-2xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60">
            {submitting?<RefreshCw size={20} className="animate-spin"/>:<Plus size={20}/>}{submitting?"Submitting…":"Submit Visit"}
          </button>
        </div>
      )}
      {view==="history" && (
        <div className="space-y-3">
          {loadingVisits?<div className="flex justify-center py-10"><RefreshCw size={24} className="animate-spin text-[#E8956D]"/></div>
          :visits.length===0?<div className="bg-white rounded-2xl p-8 text-center shadow-sm"><p className="font-bold text-[#3A322C]">No visits yet</p></div>
          :visits.map(v=>(
            <div key={v.id} className="bg-white rounded-2xl shadow-sm p-5">
              <div className="flex items-start justify-between"><div><p className="font-bold text-[#3A322C]">{v.school_name}</p><p className="text-xs text-[#8A7E73]">{v.visit_type} · {v.visit_date}</p></div><span className="text-xs bg-[#EDF2EE] text-[#637C64] font-bold px-2 py-1 rounded-full">Rt {v.route_number}</span></div>
              {v.observations&&<p className="text-sm text-[#5C5147] mt-3">{v.observations}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══ SCHOOLS + MAP TAB ═══════════════════════════════════════════════════════
function SchoolsTab({ isAdmin }) {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapView, setMapView] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const mapRef = useRef(null); const gmapRef = useRef(null); const markersRef = useRef({});
  const [mapReady, setMapReady] = useState(false);
  const [locationMsg, setLocationMsg] = useState("");

  useEffect(()=>{coordinatorAPI.getSchools().then(r=>setSchools(r.data?.schools||[])).catch(()=>{}).finally(()=>setLoading(false));},[]);

  useEffect(()=>{
    if(!mapView)return;
    const check=setInterval(()=>{if(window.google?.maps){clearInterval(check);setMapReady(true);}},100);
    setTimeout(()=>clearInterval(check),10000);
    return()=>clearInterval(check);
  },[mapView]);

  useEffect(()=>{
    if(!mapReady||!mapRef.current||gmapRef.current)return;
    const map=new window.google.maps.Map(mapRef.current,{center:HYD,zoom:11,mapTypeId:"satellite"});
    gmapRef.current=map;
    const withCoords=schools.filter(s=>s.lat&&s.lng);
    const bounds=withCoords.length?new window.google.maps.LatLngBounds():null;
    withCoords.forEach(s=>{
      const marker=new window.google.maps.Marker({position:{lat:parseFloat(s.lat),lng:parseFloat(s.lng)},map,title:s.school_name,icon:{path:window.google.maps.SymbolPath.CIRCLE,scale:9,fillColor:"#22c55e",fillOpacity:1,strokeColor:"#fff",strokeWeight:2}});
      const info=new window.google.maps.InfoWindow({content:`<div style="font-family:sans-serif;padding:4px"><strong>${s.school_name}</strong><br/>Route ${s.route_number}</div>`});
      marker.addListener("click",()=>{info.open(map,marker);setSelected(s);});
      markersRef.current[s.id]=marker;
      if(bounds)bounds.extend({lat:parseFloat(s.lat),lng:parseFloat(s.lng)});
    });
    if(bounds&&withCoords.length)map.fitBounds(bounds);
  },[mapReady]); // eslint-disable-line

  const withCoords=schools.filter(s=>s.lat&&s.lng);
  const filtered=schools.filter(s=>!search||s.school_name.toLowerCase().includes(search.toLowerCase())||String(s.route_number).includes(search));

  if(loading)return<div className="flex justify-center py-16"><RefreshCw size={28} className="animate-spin text-[#E8956D]"/></div>;
  return (
    <div className="space-y-4">
      <div className="flex gap-2 p-1 bg-white rounded-2xl shadow-sm">
        {[["list","List",List],["map","Map",MapIcon]].map(([v,l,Icon])=>(
          <button key={v} onClick={()=>setMapView(v==="map")} className={`flex-1 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 ${(v==="map")===mapView?"bg-[#E8956D] text-white shadow":"text-[#8A7E73]"}`}><Icon size={14}/>{l}</button>
        ))}
      </div>
      <input className="w-full border-2 border-[#E5E1DB] rounded-xl px-4 py-3.5 text-base outline-none focus:border-[#E8956D]" placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)}/>
      {locationMsg&&<div className="bg-[#EDF2EE] text-[#637C64] rounded-2xl px-4 py-2.5 font-bold text-sm">{locationMsg}</div>}
      {mapView?(
        <div className="space-y-3">
          <div className="bg-white rounded-2xl px-4 py-3 shadow-sm flex items-center justify-between">
            <p className="text-sm font-bold text-[#3A322C]">{withCoords.length} of {schools.length} schools pinned</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden" style={{height:"380px"}}>
            <div ref={mapRef} className="w-full h-full"/>
            {withCoords.length===0&&<div className="absolute inset-0 bg-[#F4F3F0]/90 flex items-center justify-center rounded-2xl"><div className="text-center px-6 py-8"><MapPin size={40} className="mx-auto mb-3 text-[#C4BAAF]"/><p className="font-bold text-[#3A322C]">No locations set yet</p><p className="text-sm text-[#8A7E73] mt-2">Ask admin to pin schools in School Master.</p></div></div>}
          </div>
          {selected&&<div className="bg-white rounded-2xl shadow-sm p-5"><p className="font-bold text-[#3A322C]">{selected.school_name}</p><p className="text-sm text-[#8A7E73]">Route {selected.route_number} · {selected.mandal}</p></div>}
        </div>
      ):(
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="divide-y divide-[#F4F3F0]">
            {filtered.map(s=>(
              <div key={s.id} className="flex items-center px-5 py-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xs font-bold mr-4 ${s.has_coordinates?"bg-[#E8956D]/10 text-[#E8956D]":"bg-[#F4F3F0] text-[#8A7E73]"}`}>{s.route_number}</div>
                <div className="flex-1 min-w-0"><p className="font-medium text-[#3A322C] text-sm truncate">{s.school_name}</p><p className="text-xs text-[#8A7E73]">{s.mandal}</p></div>
                {s.has_coordinates?<Navigation size={14} className="text-[#637C64]"/>:<span className="text-[10px] text-[#C4BAAF]">No pin</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ══ COMPLAINTS TAB ══════════════════════════════════════════════════════════
function ComplaintsTab() {
  const [data, setData] = useState(null); const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({severity:"",feedback_category:""});
  const [expanded, setExpanded] = useState(null); const [rcNotes, setRcNotes] = useState({}); const [saving, setSaving] = useState(null);

  const fetch = useCallback(async()=>{
    setLoading(true);
    try{const p={};if(filter.severity)p.severity=filter.severity;if(filter.feedback_category)p.feedback_category=filter.feedback_category;const r=await coordinatorAPI.getComplaints(p);setData(r.data);}catch(e){}
    setLoading(false);
  },[filter.severity,filter.feedback_category]);

  useEffect(()=>{fetch();},[fetch]);

  const save=async(id)=>{setSaving(id);try{await coordinatorAPI.addRcNotes(id,{rc_notes:rcNotes[id]||""});await fetch();}catch(e){}setSaving(null);};

  return (
    <div className="space-y-4">
      {data?.summary&&(
        <div className="grid grid-cols-3 gap-3">
          {[{l:"This Month",v:data.summary.total_this_month,c:"text-[#C2624E]"},{l:"Most Affected",v:data.summary.most_affected_school?.split(" ")[0]||"—",c:"text-[#5C5147]"},{l:"Top Category",v:(data.summary.most_common_category||"—").split(" ")[0],c:"text-[#8A7E73]"}].map(s=>(
            <div key={s.l} className="bg-white rounded-2xl p-3 text-center shadow-sm"><p className={`text-lg font-extrabold ${s.c} truncate`}>{s.v}</p><p className="text-[10px] text-[#8A7E73]">{s.l}</p></div>
          ))}
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        <select className="flex-1 min-w-[100px] border border-[#E5E1DB] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={filter.severity} onChange={e=>setFilter(f=>({...f,severity:e.target.value}))}>
          {["","Low","Medium","High"].map(s=><option key={s} value={s}>{s||"All Severity"}</option>)}
        </select>
        <select className="flex-1 min-w-[100px] border border-[#E5E1DB] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={filter.feedback_category} onChange={e=>setFilter(f=>({...f,feedback_category:e.target.value}))}>
          {["","Food Quality","Quantity","Delivery Time","Vessel Condition","Staff Behaviour"].map(c=><option key={c} value={c}>{c||"All Categories"}</option>)}
        </select>
      </div>
      {loading?<div className="flex justify-center py-8"><RefreshCw size={24} className="animate-spin text-[#E8956D]"/></div>
      :!data?.complaints?.length?<div className="bg-white rounded-2xl p-8 text-center shadow-sm"><Flag size={40} className="mx-auto mb-3 text-[#C4BAAF]"/><p className="font-bold text-[#3A322C]">No complaints on your routes</p></div>
      :(data.complaints||[]).map((c,i)=>(
        <div key={c.id||i} className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <button className="w-full text-left px-5 py-4 flex items-start gap-3" onClick={()=>setExpanded(expanded===c.id?null:c.id)}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap"><p className="font-bold text-[#3A322C] text-sm">{c.school_name}</p><span className="text-[10px] bg-[#E8956D]/10 text-[#E8956D] font-bold px-1.5 py-0.5 rounded-full">Rt {c.route_number}</span></div>
              <p className="text-xs text-[#8A7E73]">{fmtDate(c.created_at)}</p>
              <div className="flex gap-1 flex-wrap mt-1">
                {c.feedback_category&&<span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${CAT[c.feedback_category]||"bg-[#F4F3F0] text-[#5C5147]"}`}>{c.feedback_category}</span>}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${SEV[c.complaint_severity]||SEV.Low}`}>{c.complaint_severity}</span>
              </div>
            </div>
            {expanded===c.id?<ChevronUp size={16} className="text-[#C4BAAF] mt-1"/>:<ChevronDown size={16} className="text-[#C4BAAF] mt-1"/>}
          </button>
          {expanded===c.id&&(
            <div className="px-5 pb-5 space-y-3 border-t border-[#F4F3F0]">
              {c.notes&&<p className="text-sm text-[#5C5147] pt-3 bg-[#FAFAF8] rounded-xl px-4 py-3">{c.notes}</p>}
              <textarea rows={2} className="w-full border border-[#E5E1DB] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#E8956D] resize-none" placeholder="Add your field notes…" value={rcNotes[c.id]||c.rc_notes||""} onChange={e=>setRcNotes(n=>({...n,[c.id]:e.target.value}))}/>
              <button onClick={()=>save(c.id)} disabled={saving===c.id} className="bg-[#E8956D] text-white rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-60">{saving===c.id?"Saving…":"Save Notes"}</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ══ PERFORMANCE TAB ══════════════════════════════════════════════════════════
function PerformanceTab() {
  const [data, setData] = useState(null); const [loading, setLoading] = useState(true);

  useEffect(()=>{coordinatorAPI.getPerformance(30).then(r=>setData(r.data)).catch(()=>{}).finally(()=>setLoading(false));},[]);

  if(loading)return<div className="flex justify-center py-16"><RefreshCw size={28} className="animate-spin text-[#E8956D]"/></div>;
  if(!data?.routes)return<div className="bg-white rounded-2xl p-8 text-center shadow-sm"><p className="font-bold text-[#3A322C]">No data yet</p></div>;

  const d = data;
  const kpis = [
    {label:"On-Time Dispatches",value:`${d.dispatches.on_time_pct}%`,sub:`${d.dispatches.on_time} of ${d.dispatches.total}`,color:"text-[#E8956D]"},
    {label:"School Coverage",value:`${d.visits.coverage_pct}%`,sub:`${d.visits.schools_covered} of ${d.visits.schools_total} schools`,color:"text-[#637C64]"},
    {label:"Complaints Resolved",value:`${d.complaints.resolution_pct}%`,sub:`${d.complaints.resolved} of ${d.complaints.total}`,color:"text-blue-600"},
    {label:"Total Visits",value:d.visits.total,sub:"last 30 days",color:"text-purple-600"},
  ];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-extrabold text-[#3A322C] text-xl">My Performance</h2>
        <p className="text-xs text-[#8A7E73] mt-0.5">Last 30 days · {d.routes} routes</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {kpis.map(k=>(
          <div key={k.label} className="bg-white rounded-2xl p-4 shadow-sm">
            <p className={`text-3xl font-extrabold ${k.color}`}>{k.value}</p>
            <p className="text-xs font-bold text-[#3A322C] mt-1">{k.label}</p>
            <p className="text-[10px] text-[#8A7E73]">{k.sub}</p>
          </div>
        ))}
      </div>

      {d.weekly_visit_trend?.length>0&&(
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <p className="font-bold text-[#3A322C] text-sm mb-3">Weekly Visit Trend</p>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={d.weekly_visit_trend}>
              <Bar dataKey="visits" fill="#E8956D" radius={[3,3,0,0]}/>
              <XAxis dataKey="week" tick={{fontSize:10}} axisLine={false} tickLine={false}/>
              <Tooltip formatter={v=>[v,"Visits"]} contentStyle={{fontSize:"11px",padding:"4px 8px"}}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {Object.keys(d.visits.by_type||{}).length>0&&(
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <p className="font-bold text-[#3A322C] text-sm mb-3">Visit Types</p>
          <div className="space-y-2">
            {Object.entries(d.visits.by_type).map(([type,count])=>{
              const total=Object.values(d.visits.by_type).reduce((s,v)=>s+v,0);
              const pct=Math.round(count/total*100);
              return (
                <div key={type}>
                  <div className="flex justify-between text-xs mb-1"><span className="font-medium text-[#3A322C]">{type}</span><span className="text-[#8A7E73]">{count}</span></div>
                  <div className="h-2 bg-[#F4F3F0] rounded-full overflow-hidden"><div className="h-full bg-[#E8956D] rounded-full" style={{width:`${pct}%`}}/></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm p-5">
        <p className="font-bold text-[#3A322C] text-sm mb-3">Complaints</p>
        <div className="grid grid-cols-2 gap-3 text-center">
          <div><p className="text-3xl font-extrabold text-[#C2624E]">{d.complaints.total}</p><p className="text-xs text-[#8A7E73]">Total complaints</p></div>
          <div><p className="text-3xl font-extrabold text-[#637C64]">{d.complaints.resolved}</p><p className="text-xs text-[#8A7E73]">Resolved</p></div>
        </div>
      </div>
    </div>
  );
}

// ══ MAIN DASHBOARD ═══════════════════════════════════════════════════════════
export default function CoordinatorDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("home");
  const [myRoutes, setMyRoutes] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const isAdmin = user?.is_admin||["Super Admin","Administration","Semi- Admin"].includes(user?.role_name||"");

  useEffect(()=>{coordinatorAPI.getMyRoutes().then(r=>setMyRoutes(r.data)).catch(()=>{});},[]);

  useEffect(()=>{
    const poll=async()=>{try{const r=await coordinatorAPI.getAlerts();const a=r.data?.alerts||[];if(a.length>alerts.length)setAlertDismissed(false);setAlerts(a);}catch(e){}};
    poll();const t=setInterval(poll,15000);return()=>clearInterval(t);
  },[]); // eslint-disable-line

  const ackAll=async()=>{await Promise.all(alerts.map(a=>coordinatorAPI.acknowledgeAlert(a.alert_id).catch(()=>{})));setAlerts([]);setAlertDismissed(true);setTab("complaints");};
  const unacked=alerts.filter(a=>!a.acknowledged);

  const TABS = [
    {id:"home",      icon:Activity,    label:"Home"},
    {id:"dispatch",  icon:Truck,       label:"Dispatch"},
    {id:"vessels",   icon:CheckSquare, label:"Vessels"},
    {id:"visits",    icon:MapPin,      label:"Visits"},
    {id:"complaints",icon:Flag,        label:"Issues"},
    {id:"schools",   icon:List,        label:"Schools"},
    {id:"perf",      icon:BarChart3,   label:"My Stats"},
  ];

  return (
    <div className="min-h-screen bg-[#F7F5F2] flex flex-col" data-testid="coordinator-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E1DB] px-4 py-3 flex items-center justify-between sticky top-0 z-10 shadow-sm flex-shrink-0">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="h-9 w-9 rounded-full object-cover"/>
          <div>
            <p className="font-extrabold text-[#3A322C] text-base">Manna Trust</p>
            <p className="text-[11px] text-[#8A7E73]">Route Coordinator Portal</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unacked.length>0&&!alertDismissed&&(
            <button onClick={ackAll} className="relative bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 animate-pulse">
              <AlertTriangle size={12}/> {unacked.length}
            </button>
          )}
          <button onClick={logout} className="p-2 rounded-xl text-[#8A7E73] hover:text-[#C2624E] hover:bg-[#FBEAE7] min-h-[44px] min-w-[44px] flex items-center justify-center">
            <LogOut size={18}/>
          </button>
        </div>
      </header>

      {/* Alert banner */}
      {unacked.length>0&&!alertDismissed&&(
        <div className="bg-red-600 text-white px-4 py-3 flex items-center gap-3 z-10" data-testid="alert-banner">
          <AlertTriangle size={16} className="flex-shrink-0"/>
          <p className="text-sm font-bold flex-1 cursor-pointer" onClick={ackAll}>
            {unacked.length} alert{unacked.length>1?"s":""}: {unacked[0]?.school_name} — {unacked[0]?.feedback_category} ({unacked[0]?.severity})
          </p>
          <button onClick={()=>setAlertDismissed(true)}><X size={16}/></button>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 overflow-y-auto px-4 pt-4 pb-28">
        {tab==="home"       && <HomeTab myRoutes={myRoutes}/>}
        {tab==="dispatch"   && <DispatchTab myRoutes={myRoutes}/>}
        {tab==="vessels"    && <VesselsTab/>}
        {tab==="visits"     && <VisitsTab myRoutes={myRoutes}/>}
        {tab==="complaints" && <ComplaintsTab/>}
        {tab==="schools"    && <SchoolsTab isAdmin={isAdmin}/>}
        {tab==="perf"       && <PerformanceTab/>}
      </main>

      {/* Bottom navigation — 7 tabs, small labels */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E1DB] flex z-20 shadow-lg">
        {TABS.map(t=>(
          <button key={t.id}
            onClick={()=>{setTab(t.id);if(t.id==="complaints")setAlertDismissed(true);}}
            className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 min-h-[56px] relative transition-colors ${tab===t.id?"text-[#E8956D]":"text-[#8A7E73]"}`}
            data-testid={`tab-${t.id}`}>
            <t.icon size={18} strokeWidth={tab===t.id?2.5:1.5}/>
            {t.id==="complaints"&&unacked.length>0&&(
              <span className="absolute top-1 right-[10%] w-4 h-4 bg-red-500 rounded-full text-[9px] text-white font-bold flex items-center justify-center">{unacked.length}</span>
            )}
            <span className="text-[9px] font-bold leading-tight">{t.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
